import { useCallback, useMemo, useState } from 'react';

import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { Table } from '@tanstack/react-table';
import { UseFormReturn } from 'react-hook-form';

import { FilterValues } from '@/hooks/use-filter-values';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import {
  hashItemsForStaleness,
  mergeLeafWithFormEdits,
  usePollingStaleness,
} from '@/modules/finance/common';

import { getGenerateInvoiceData } from '../api';
import { getInvoiceProjectPath } from '../constants/generate-invoices-table-columns';
import {
  GenerateInvoiceListItem,
  GenerateInvoiceProjectTeamMemberListItem,
  InvoiceHoursSourceType,
} from '../types';
import { GenerateInvoicesTableFormValues } from '../validations/generate-invoices-validation';
import { GENERATE_INVOICE_DATA_QUERY_KEY } from './use-generate-inovice-data';

/**
 * The mapper collapses `hoursSources` into a single primary source (TRACKED)
 * for display, so `member.hours`/`effectiveRate`/`total` alone hide changes in
 * BOOKED/CAPACITY/APPROVED etc. We hash every source instead.
 */
const extractInvoiceStaleFields = (item: GenerateInvoiceListItem) => ({
  count: item.projectMembers?.length ?? 0,
  members: (item.projectMembers ?? []).map(m => ({
    id: m.id,
    sources: (m.hoursSources ?? []).map(s => ({
      type: s.sourceType,
      h: s.hours,
      r: s.averageRate,
      a: s.amount,
    })),
  })),
  // Scheduled (server-provided) overheads participate in staleness: a new
  // PENDING income overhead appearing for the period, or an existing one
  // being edited/invoiced elsewhere, should flip the refresh indicator.
  overheads: (item.projectOverheads ?? [])
    .filter(o => o.overheadId !== undefined)
    .map(o => ({ id: o.overheadId, d: o.description, a: o.effectiveRate, dt: o.date })),
});

const hashInvoiceItems = (items: GenerateInvoiceListItem[] | undefined): string =>
  hashItemsForStaleness(
    (items ?? []).map(it => ({ ...it, id: it.project.id })),
    item => extractInvoiceStaleFields(item as unknown as GenerateInvoiceListItem)
  );

/**
 * A value is "auto-derived" if it matches any value in the PREVIOUS server
 * `hoursSources` for that field — meaning the user selected a source rather
 * than typing a custom number. Auto-derived values follow the latest server
 * response on refresh; typed values are preserved.
 */
const isAutoDerivedValue = (
  value: number | undefined,
  sources: GenerateInvoiceProjectTeamMemberListItem['hoursSources'],
  field: 'hours' | 'averageRate'
): boolean => {
  if (value == null) return true;
  return sources.some(source => source[field] === value);
};

interface UseSmartRefreshParams {
  filters: FilterValues['values'];
  apiFilters: Parameters<typeof getGenerateInvoiceData>[0]['filters'];
  enabled: boolean;
  /** The items currently shown (from the authoritative list query). */
  items: GenerateInvoiceListItem[] | undefined;
}

interface UseSmartRefreshResult {
  hasStaleData: boolean;
  isRefreshing: boolean;
  applySmartRefresh: (
    form: UseFormReturn<GenerateInvoicesTableFormValues>,
    table: Table<GenerateInvoiceListItem>
  ) => Promise<void>;
}

export const useGenerateInvoicesSmartRefresh = ({
  filters,
  apiFilters,
  enabled,
  items,
}: UseSmartRefreshParams): UseSmartRefreshResult => {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const filterFingerprint = useMemo(() => JSON.stringify(filters), [filters]);
  const authoritativeQueryKey = useMemo(
    () => [GENERATE_INVOICE_DATA_QUERY_KEY, filters] as const,
    [filters]
  );
  const peekQueryKey = useMemo(
    () => [GENERATE_INVOICE_DATA_QUERY_KEY, filters, 'peek'] as const,
    [filters]
  );

  const { hasStaleData, latestPeekRef, markAccepted } =
    usePollingStaleness<GenerateInvoiceListItem>({
      enabled,
      filterFingerprint,
      authoritativeItems: items,
      peekQueryKey,
      fetchPeek: () => getGenerateInvoiceData({ filters: apiFilters }),
      hashItems: hashInvoiceItems,
    });

  const applySmartRefresh = useCallback<UseSmartRefreshResult['applySmartRefresh']>(
    async (form, table) => {
      const fresh = latestPeekRef.current;
      if (!fresh) return;

      setIsRefreshing(true);
      try {
        const cached =
          queryClient.getQueryData<InfiniteData<PaginatedResponse<GenerateInvoiceListItem>>>(
            authoritativeQueryKey
          );
        const oldItems = cached?.pages?.flatMap(page => page.items) ?? [];
        // Match projects/members by STABLE ID, not array position — the form is id-keyed and
        // the server may reorder or drop rows between polls.
        const oldByProject = new Map(oldItems.map(p => [p.project.id, p]));

        const mergedItems = fresh.map(project => {
          const oldProject = oldByProject.get(project.project.id);
          if (!oldProject) return project;
          const oldMemberById = new Map((oldProject.projectMembers ?? []).map(m => [m.id, m]));
          const base = getInvoiceProjectPath(project.project.id);

          const mergedMembers = (project.projectMembers ?? []).map(newMember => {
            const oldMember = oldMemberById.get(newMember.id);
            if (!oldMember) return newMember;

            const leaf = `${base}.member.m${newMember.id}` as const;
            const oldSources = oldMember.hoursSources ?? [];
            const newSources = newMember.hoursSources ?? [];

            const selectedSource =
              (form.getValues(`${leaf}.invoicedBy` as never) as
                | InvoiceHoursSourceType
                | undefined) ?? oldMember.invoiceBy;
            const newPrimary =
              newSources.find(s => s.sourceType === selectedSource) ??
              newSources.find(s => s.sourceType === InvoiceHoursSourceType.TRACKED_BILLABLE) ??
              newSources[0];

            const currentHours =
              (form.getValues(`${leaf}.hours` as never) as number | undefined) ?? oldMember.hours;
            const currentRate =
              (form.getValues(`${leaf}.effectiveRate` as never) as number | undefined) ??
              oldMember.effectiveRate;

            const { hours, effectiveRate, total } = mergeLeafWithFormEdits({
              newPrimaryHours: newPrimary?.hours,
              newPrimaryRate: newPrimary?.averageRate,
              newPrimaryAmount: newPrimary?.amount,
              currentHours,
              currentRate,
              hoursAutoDerived: isAutoDerivedValue(currentHours, oldSources, 'hours'),
              rateAutoDerived: isAutoDerivedValue(currentRate, oldSources, 'averageRate'),
            });

            form.setValue(`${leaf}.hours` as never, hours as never, {
              shouldDirty: false,
              shouldValidate: false,
            });
            form.setValue(`${leaf}.effectiveRate` as never, effectiveRate as never, {
              shouldDirty: false,
              shouldValidate: false,
            });

            return { ...newMember, invoiceBy: selectedSource, hours, effectiveRate, total };
          });

          // Scheduled overheads: untouched form values (still equal to the
          // previous server value) follow the latest server data; user-typed
          // edits are preserved. Rows that disappeared server-side (invoiced /
          // deleted elsewhere) drop out with the fresh items automatically.
          const oldOverheadById = new Map(
            (oldProject.projectOverheads ?? [])
              .filter(o => o.overheadId !== undefined)
              .map(o => [o.overheadId, o])
          );
          (project.projectOverheads ?? []).forEach(newOverhead => {
            if (newOverhead.overheadId === undefined) return;
            const oldOverhead = oldOverheadById.get(newOverhead.overheadId);
            if (!oldOverhead) return;
            const leaf = `${base}.overhead.o${newOverhead.id}` as const;

            const currentDescription = form.getValues(`${leaf}.description` as never) as
              | string
              | undefined;
            if (
              currentDescription === undefined ||
              currentDescription === oldOverhead.description
            ) {
              form.setValue(`${leaf}.description` as never, newOverhead.description as never, {
                shouldDirty: false,
                shouldValidate: false,
              });
            }

            const currentRate = form.getValues(`${leaf}.effectiveRate` as never) as
              | number
              | undefined;
            if (currentRate === undefined || currentRate === oldOverhead.effectiveRate) {
              form.setValue(`${leaf}.effectiveRate` as never, newOverhead.effectiveRate as never, {
                shouldDirty: false,
                shouldValidate: false,
              });
            }
          });

          return { ...project, projectMembers: mergedMembers };
        });

        queryClient.setQueryData<InfiniteData<PaginatedResponse<GenerateInvoiceListItem>>>(
          authoritativeQueryKey,
          () => ({
            pages: [
              new PaginatedResponse({
                items: mergedItems,
                pagination: { total: mergedItems.length, cursor: null },
              }),
            ],
            pageParams: [undefined],
          })
        );

        // TanStack drops selection state for any row whose id disappears from the new data
        // automatically — no manual cleanup needed here.
        void table;

        // Hash the freshly-received server items, not the merged result — the merged result
        // includes user edits the server doesn't know about, which would mask the next poll.
        markAccepted(fresh);
      } finally {
        setIsRefreshing(false);
      }
    },
    [authoritativeQueryKey, latestPeekRef, markAccepted, queryClient]
  );

  return useMemo(
    () => ({ hasStaleData, isRefreshing, applySmartRefresh }),
    [hasStaleData, isRefreshing, applySmartRefresh]
  );
};
